import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ticketsApi } from '../../api/tickets';
import { Search } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import { StatusBadge, PriorityBadge } from '../../components/badges';

export default function PortalTicketList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-tickets', { search, status, page }],
    // Filter by semantic category — companies can rename/add statuses, but
    // categories are fixed, so this works for every tenant.
    queryFn: () => ticketsApi.list({ search, statusCategory: status || undefined, page, limit: 15 }),
  });

  const tickets = data?.tickets ?? [];
  const totalPages = data?.pages ?? 1;

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="My Tickets"
        intro="Track the tickets you have opened. Click a row to read the thread and reply."
        actions={
          <Link to="/portal/tickets/new" className="btn btn-primary">
            New Ticket
          </Link>
        }
      />

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="search"
              placeholder="Search tickets..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="input pl-9"
            />
          </div>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="select w-44"
          >
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="open">Open</option>
            <option value="waiting_customer">Waiting on me</option>
            <option value="waiting_internal">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Ticket list */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {isLoading ? (
          <div className="py-12 text-center text-muted">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <EmptyState>
            No tickets found. Use <b>New Ticket</b> to create your first one.
          </EmptyState>
        ) : (
          <>
            {/* Mobile: stacked ticket cards — the table is unreadable at phone width */}
            <div className="sm:hidden">
              {tickets.map((t: any) => (
                <div
                  key={t.id}
                  className="p-3 space-y-1.5 border-b border-line-light active:bg-row-odd"
                  onClick={() => navigate(`/portal/tickets/${t.id}`)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-bold text-primary text-sm">#{t.ticketNumber}</span>
                    <StatusBadge status={t.status} />
                    <PriorityBadge priority={t.priority} />
                  </div>
                  <p className="font-medium text-fg text-sm">{t.title}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                    {t.category?.name && <span>{t.category.name}</span>}
                    {t.createdAt && <span>{new Date(t.createdAt).toLocaleDateString()}</span>}
                  </div>
                </div>
              ))}
            </div>
          <table className="scpa-table hidden sm:table">
            <thead>
              <tr>
                <th className="scpa-th">#</th>
                <th className="scpa-th">Title</th>
                <th className="scpa-th">Priority</th>
                <th className="scpa-th">Category</th>
                <th className="scpa-th">Created</th>
                <th className="scpa-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t: any) => (
                <tr
                  key={t.id}
                  className="scpa-row"
                  onClick={() => navigate(`/portal/tickets/${t.id}`)}
                >
                  <td className="scpa-td">
                    <span className="font-mono font-bold text-primary">#{t.ticketNumber}</span>
                  </td>
                  <td className="scpa-td">
                    <span className="font-medium text-fg">{t.title}</span>
                  </td>
                  <td className="scpa-td">
                    <PriorityBadge priority={t.priority} />
                  </td>
                  <td className="scpa-td text-muted">{t.category?.name ?? '-'}</td>
                  <td className="scpa-td text-muted">
                    {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="scpa-td">
                    <StatusBadge status={t.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
